// How many units actually left a cart.
//
// What was taken back OUT of a cart is not derivable from the cart itself: the
// cart is what is LEFT. Only the moment of removal can record it, and only by
// comparing the carts either side of the call.
//
// The quantity is DIFFED rather than taken from the caller's arguments. One
// variant can occupy several lines (two engravings of the same mug), and how many
// of them a removal touches is the server's decision, not the client's — a
// subtraction is right whatever that decision is, and stays right if it changes.
//
// A quantity DECREASE is a removal too: 5 → 2 is three units taken out. Counting
// only outright removals would miss the commonest way a cart shrinks.

import type { Cart, CartLine } from "./api"

/** Selects the lines a single cart operation could have touched. */
export type LineMatch = (l: CartLine) => boolean

export const byVariant =
  (variantID: number): LineMatch =>
  (l) =>
    l.variant_id === variantID

export const byLine =
  (lineID: number): LineMatch =>
  (l) =>
    l.line_id === lineID

/** Total units matching, across every line that matches. */
export function unitsOf(cart: Cart | null | undefined, match: LineMatch): number {
  return (cart?.lines ?? []).filter(match).reduce((n, l) => n + l.quantity, 0)
}

/** Units that left, or 0 when nothing did.
 *
 *  Never negative: a failed call that leaves the cart unchanged, or a server that
 *  ADDS on one of these paths, must not be reported as a removal. */
export function unitsRemoved(before: Cart | null | undefined, after: Cart | null | undefined, match: LineMatch): number {
  const gone = unitsOf(before, match) - unitsOf(after, match)
  return gone > 0 ? gone : 0
}

/** The line as it stood BEFORE the call — what the event describes, since after
 *  it there may be no line left to describe. */
export function lineBefore(before: Cart | null | undefined, match: LineMatch): CartLine | undefined {
  return (before?.lines ?? []).find(match)
}
